import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEdit2, FiKey, FiPlus, FiX } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";

import AdminLayout from "@/layouts/AdminLayout";
import ModuleAccessCard from "@/components/shared/ModuleAccessCard";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { useAffiliatePartner, useAffiliatePartners } from "@/api/features/affiliate-partners/queries";

type AffiliateStatusFilter = "" | "PENDING" | "APPROVED" | "REJECTED";

type AffiliateFilters = {
  companyName?: string;
  contact?: string;
  status?: AffiliateStatusFilter;
};

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((part, idx) => ({
    href: `/${parts.slice(0, idx + 1).join("/")}`,
    label: part
      .split("-")
      .map((segment) => segment[0]?.toUpperCase() + segment.slice(1))
      .join(" "),
  }));

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((crumb, index) => (
          <li key={crumb.href} className="flex items-center gap-1">
            <span>/</span>
            {index < crumbs.length - 1 ? (
              <Link to={crumb.href} className="hover:text-gray-700">
                {crumb.label}
              </Link>
            ) : (
              <span className="text-gray-700">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

function ModulesDialog({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-5xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close module access dialog"
            >
              <FiX />
            </button>
          </div>
          <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function ViewAffiliatePartners() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AffiliateFilters>({});

  // Reset to first page whenever a column filter changes.
  useEffect(() => {
    setPage(1);
  }, [filters.companyName, filters.contact, filters.status]);

  const query = useMemo(
    () => ({
      page,
      limit: 10,
      companyName: filters.companyName?.trim() || undefined,
      contact: filters.contact?.trim() || undefined,
      status: filters.status || undefined,
    }),
    [page, filters.companyName, filters.contact, filters.status]
  );

  const { data, isLoading, isError } = useAffiliatePartners(query);
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 10));
  const [modulesRow, setModulesRow] = useState<(typeof rows)[number] | null>(null);
  const modulesDetailQuery = useAffiliatePartner(modulesRow?.id ?? null);

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Affiliate Partners</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/affiliates/new")}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          <FiPlus />
          Add Affiliate Partner
        </button>
      </div>

      {/* Square bottom: the pagination bar below completes this card. */}
      <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-3.5 font-semibold">S.No</th>
                <th className="px-5 py-3.5 font-semibold">
                  <div className="flex items-center gap-1.5">
                    <span>Company</span>
                    <ColumnFilter label="Company">
                      <Input
                        placeholder="Contains…"
                        value={filters.companyName || ""}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, companyName: e.target.value }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </th>
                <th className="px-5 py-3.5 font-semibold">
                  <div className="flex items-center gap-1.5">
                    <span>Contact</span>
                    <ColumnFilter label="Contact">
                      <Input
                        placeholder="Name or email…"
                        value={filters.contact || ""}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, contact: e.target.value }))
                        }
                      />
                    </ColumnFilter>
                  </div>
                </th>
                <th className="px-5 py-3.5 font-semibold">Products</th>
                <th className="px-5 py-3.5 font-semibold">Commission</th>
                <th className="px-5 py-3.5 font-semibold">
                  <div className="flex items-center gap-1.5">
                    <span>Status</span>
                    <ColumnFilter label="Status">
                      <select
                        value={filters.status || ""}
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            status: e.target.value as AffiliateStatusFilter,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                      >
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                      </select>
                    </ColumnFilter>
                  </div>
                </th>
                <th className="px-5 py-3.5 font-semibold">Created</th>
                <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-500">
                    Loading affiliate partners…
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-sm text-red-600">
                    Failed to load affiliate partners.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-500">
                    No affiliate partners found.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50/70">
                    <td className="px-5 py-4 text-gray-600">{(page - 1) * 10 + index + 1}</td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{row.companyName}</div>
                      <div className="text-xs text-gray-500">{row.affiliateCode || "Code on approval"}</div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      <div>
                        {row.contactFirstName} {row.contactLastName}
                      </div>
                      <div className="text-xs text-gray-500">{row.contactEmail}</div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {row.products.map((product) => product.name).join(", ") || "—"}
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {row.commissionPercent ? `${row.commissionPercent}%` : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                          row.status === "APPROVED"
                            ? "bg-emerald-100 text-emerald-800"
                            : row.status === "REJECTED"
                              ? "bg-rose-100 text-rose-800"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{formatDate(row.createdAt)}</td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setModulesRow(row)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                            aria-label="Manage module access"
                            title="Manage module access"
                          >
                            <FiKey />
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/affiliates/edit/${row.id}`, { state: { affiliate: row } })}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                            aria-label="Edit affiliate partner"
                            title="Edit affiliate partner"
                          >
                            <FiEdit2 />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 rounded-b-2xl border border-t-0 border-gray-200 bg-white px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing <span className="font-medium text-gray-700">{total === 0 ? 0 : (page - 1) * 10 + 1}</span> to{" "}
          <span className="font-medium text-gray-700">{Math.min(page * 10, total)}</span> of{" "}
          <span className="font-medium text-gray-700">{total}</span> results
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <ModulesDialog
        open={!!modulesRow}
        title={
          modulesRow
            ? `Module Access - ${modulesRow.companyName}`
            : "Module Access"
        }
        onClose={() => setModulesRow(null)}
      >
        {modulesDetailQuery.isLoading ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
            Loading affiliate partner...
          </div>
        ) : modulesDetailQuery.isError || !modulesDetailQuery.data ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-700">
            Failed to load affiliate partner details.
          </div>
        ) : modulesDetailQuery.data.userId ? (
          <ModuleAccessCard
            userId={modulesDetailQuery.data.userId}
            entityLabel="Affiliate Partner"
          />
        ) : (
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-6 text-sm text-amber-700">
            Module access will be available after this affiliate partner has a linked login user.
          </div>
        )}
      </ModulesDialog>
    </AdminLayout>
  );
}
